import { Brand, hasMergeConflicts } from '@principle-theorem/principle-core';
import {
  IBrand,
  IPractice,
  ISterilisationMachine,
} from '@principle-theorem/principle-core/interfaces';
import {
  DestinationEntityRecordStatus,
  FailedDestinationEntityRecord,
  ICustomMappingHandler,
  IDestinationEntity,
  IDestinationEntityRecord,
  IGetRecordResponse,
  IHasSourceIdentifier,
  IMigratedDataSummary,
  ITranslationMap,
  MergeConflictDestinationEntityRecord,
  IPracticeMigration,
  ISourceEntityHandler,
  ISourceEntityRecord,
  ISourceEntityTypes,
} from '@principle-theorem/practice-migrations/interfaces';
import {
  DocumentReference,
  Firestore,
  FirestoreMigrate,
  PrimaryKey,
  Timestamp,
  WithPrimaryKey,
  WithRef,
  toTimestamp,
} from '@principle-theorem/shared';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { TranslationMapHandler } from '../../translation-map';
import { BaseDestinationEntity } from '../base-destination-entity';
import { DestinationEntity } from '../destination-entity';
import { STERILISATION_MACHINE_RESOURCE_TYPE } from '../../mappings/sterilisation-machine';

export const STERILISATION_MACHINE_DESTINATION_ENTITY = DestinationEntity.init({
  metadata: {
    key: STERILISATION_MACHINE_RESOURCE_TYPE,
    label: 'Sterilisation Machines',
    description: ``,
  },
});

export interface ISterilisationMachineJobData<
  SterilisationMachineRecord extends ISourceEntityTypes,
> {
  sourceSterilisationMachine: IGetRecordResponse<SterilisationMachineRecord>;
  brand: WithRef<IBrand>;
  practices: WithPrimaryKey<ITranslationMap<IPractice>>[];
  sterilisationMachineMappings: WithPrimaryKey<
    ITranslationMap<ISterilisationMachine>
  >[];
}

export interface ISterilisationMachineDestinationRecord {
  sourceRef: PrimaryKey<ISourceEntityRecord>;
  sterilisationMachineRef: DocumentReference<ISterilisationMachine>;
}

export interface ISterilisationMachineRecordMigrationData {
  sterilisationMachineRecord: ISterilisationMachine &
    IHasSourceIdentifier & { createdAt: Timestamp };
}

export abstract class BaseSteriliserDestinationEntity<
  SterilisationMachineRecord extends ISourceEntityTypes,
> extends BaseDestinationEntity<
  ISterilisationMachineDestinationRecord,
  ISterilisationMachineJobData<SterilisationMachineRecord>,
  ISterilisationMachineRecordMigrationData
> {
  destinationEntity = STERILISATION_MACHINE_DESTINATION_ENTITY;
  abstract sterilisationMachineSourceEntity: ISourceEntityHandler<SterilisationMachineRecord>;
  abstract sterilisationMachineCustomMapping: ICustomMappingHandler<ISterilisationMachine>;

  get sourceCountComparison(): ISourceEntityHandler<SterilisationMachineRecord> {
    return this.sterilisationMachineSourceEntity;
  }

  sourceCountDataAccessor(
    data: ISterilisationMachineJobData<SterilisationMachineRecord>
  ): PrimaryKey<ISourceEntityRecord> {
    return data.sourceSterilisationMachine.record.id;
  }

  getDestinationEntityRecordUid(
    data: ISterilisationMachineJobData<SterilisationMachineRecord>
  ): string {
    return data.sourceSterilisationMachine.record.uid;
  }

  getMigratedData$(
    record: IDestinationEntityRecord<ISterilisationMachineDestinationRecord>
  ): Observable<IMigratedDataSummary[]> {
    if (record.status !== DestinationEntityRecordStatus.Migrated) {
      return of([]);
    }

    return Firestore.doc$(record.data.sterilisationMachineRef).pipe(
      map((steriliser) => [{ label: 'Steriliser', data: steriliser }])
    );
  }

  async hasMergeConflict(
    translationMap: TranslationMapHandler,
    data: ISterilisationMachineRecordMigrationData
  ): Promise<ISterilisationMachineRecordMigrationData | undefined> {
    const existingSteriliserRef =
      await translationMap.getDestination<ISterilisationMachine>(
        data.sterilisationMachineRecord.sourceIdentifier,
        STERILISATION_MACHINE_RESOURCE_TYPE
      );
    if (!existingSteriliserRef) {
      return;
    }

    try {
      const existingSteriliser = await Firestore.getDoc(existingSteriliserRef);
      const hasMergeConflict = hasMergeConflicts(
        data.sterilisationMachineRecord,
        existingSteriliser
      );
      if (hasMergeConflict) {
        return {
          ...data,
          sterilisationMachineRecord: {
            ...existingSteriliser,
            sourceIdentifier: data.sterilisationMachineRecord.sourceIdentifier,
          },
        };
      }
    } catch {
      return;
    }
  }

  buildMergeConflictRecord(
    _migration: WithRef<IPracticeMigration>,
    _destinationEntity: WithRef<IDestinationEntity>,
    _translationMap: TranslationMapHandler,
    jobData: ISterilisationMachineJobData<SterilisationMachineRecord>,
    _migrationData: ISterilisationMachineRecordMigrationData
  ): IDestinationEntityRecord & MergeConflictDestinationEntityRecord {
    return {
      uid: jobData.sourceSterilisationMachine.record.uid,
      label: jobData.sourceSterilisationMachine.record.label,
      sourceRef: jobData.sourceSterilisationMachine.record.id,
      status: DestinationEntityRecordStatus.MergeConflict,
    };
  }

  async runJob(
    _migration: WithRef<IPracticeMigration>,
    _destinationEntity: WithRef<IDestinationEntity>,
    translationMap: TranslationMapHandler,
    data: ISterilisationMachineJobData<SterilisationMachineRecord>,
    migrationData: ISterilisationMachineRecordMigrationData
  ): Promise<IDestinationEntityRecord> {
    const sterilisationMachineMapOverride =
      data.sterilisationMachineMappings.find(
        (steriliser) =>
          steriliser.sourceIdentifier ===
          this.sterilisationMachineSourceEntity
            .getSourceRecordId(data.sourceSterilisationMachine.data)
            .toString()
      );

    if (sterilisationMachineMapOverride?.destinationIdentifier) {
      const sterilisationMachineRef =
        sterilisationMachineMapOverride.destinationIdentifier;
      await translationMap.upsert({
        sourceIdentifier:
          migrationData.sterilisationMachineRecord.sourceIdentifier,
        destinationIdentifier: sterilisationMachineRef,
        resourceType: STERILISATION_MACHINE_RESOURCE_TYPE,
      });
      return this.buildSkippedResponse(data);
    }

    const existingSterilisationMachineRef = await translationMap.getDestination(
      migrationData.sterilisationMachineRecord.sourceIdentifier,
      STERILISATION_MACHINE_RESOURCE_TYPE
    );
    const sterilisationMachineRef = await FirestoreMigrate.upsertDoc(
      Brand.sterilisationMachineCol(data.brand),
      {
        ...migrationData.sterilisationMachineRecord,
      },
      existingSterilisationMachineRef?.id
    );

    if (!existingSterilisationMachineRef) {
      await translationMap.upsert({
        sourceIdentifier:
          migrationData.sterilisationMachineRecord.sourceIdentifier,
        destinationIdentifier: sterilisationMachineRef,
        resourceType: STERILISATION_MACHINE_RESOURCE_TYPE,
      });
    }
    return this._buildSuccessResponse(
      data.sourceSterilisationMachine,
      sterilisationMachineRef
    );
  }

  protected _buildErrorResponse(
    sterilisationMachine: IGetRecordResponse<SterilisationMachineRecord>,
    errorMessage?: string
  ): IDestinationEntityRecord & FailedDestinationEntityRecord {
    return {
      uid: sterilisationMachine.record.uid,
      label: sterilisationMachine.record.label,
      status: DestinationEntityRecordStatus.Failed,
      sourceRef: sterilisationMachine.record.id,
      errorMessage: errorMessage ?? `Can't resolve fee schedule data`,
      failData: {},
    };
  }

  protected _buildSuccessResponse(
    record: IGetRecordResponse<SterilisationMachineRecord>,
    sterilisationMachineRef: DocumentReference<ISterilisationMachine>
  ): IDestinationEntityRecord<ISterilisationMachineDestinationRecord> {
    return {
      uid: record.record.uid,
      label: record.record.label,
      data: {
        sourceRef: record.record.id,
        sterilisationMachineRef,
      },
      status: DestinationEntityRecordStatus.Migrated,
      sourceRef: record.record.id,
      migratedAt: toTimestamp(),
    };
  }
}
